/**
 * @file RegisterPage.tsx
 * @description Invitation-based registration. The page expects a `?token=`
 *   query parameter (provided by the email link). On submit, sends to
 *   /auth/register; on success, navigates to /app.
 * @module Pages
 * @security The invitation token never appears in logs or in the cookie —
 *   it is consumed once and then discarded by the server.
 */

import type { FormEvent, JSX } from 'react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { z } from 'zod';
import { Button } from '../components/common/Button';
import { FormField } from '../components/common/FormField';
import { PasswordField } from '../components/common/PasswordField';
import { LanguageToggle } from '../components/common/LanguageToggle';
import { registerFromInvitation } from '../lib/auth-api';
import { ApiError } from '../lib/api';

const Schema = z.object({
  email: z.string().email(),
  password: z.string().min(12),
});

interface FieldErrors {
  email?: string;
  password?: string;
  form?: string;
}

function validateFields(email: string, password: string, t: TFunction): FieldErrors {
  const parsed = Schema.safeParse({ email, password });
  if (parsed.success) return {};
  const out: FieldErrors = {};
  for (const issue of parsed.error.issues) {
    const f = issue.path[0];
    if (f === 'email') out.email = t('login.email_invalid');
    if (f === 'password') out.password = t('login.password_too_short');
  }
  return out;
}

function mapApiError(err: unknown, t: TFunction): string {
  if (err instanceof ApiError && err.status === 400) return t('register.invalid_invitation');
  if (err instanceof ApiError && err.status === 409) return t('register.conflict');
  return t('register.generic_error');
}

export default function RegisterPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const token = search.get('token') ?? '';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (token === '') setErrors({ form: t('register.missing_token') });
  }, [token, t]);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const validation = validateFields(email, password, t);
    if (validation.email !== undefined || validation.password !== undefined) {
      setErrors(validation);
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      await registerFromInvitation({
        invitation_token: token,
        email,
        password,
        locale: i18n.language === 'en' ? 'en' : 'he',
      });
      navigate('/app');
    } catch (err) {
      setErrors({ form: mapApiError(err, t) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex flex-col">
      <header className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
        <Link to="/" className="font-semibold text-slate-900">
          {t('landing.brand')}
        </Link>
        <LanguageToggle />
      </header>
      <section className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-4" noValidate>
          <h1 className="text-2xl font-bold text-slate-900">{t('register.title')}</h1>
          <p className="text-sm text-slate-500">{t('register.subtitle')}</p>
          {errors.form !== undefined ? (
            <p role="alert" className="text-sm text-red-600">
              {errors.form}
            </p>
          ) : null}
          <FormField
            id="email"
            label={t('login.email_label')}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email}
            required
          />
          <PasswordField
            id="password"
            label={t('register.password_label')}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
            required
          />
          <Button type="submit" loading={loading} className="w-full" disabled={token === ''}>
            {t('register.submit')}
          </Button>
        </form>
      </section>
    </main>
  );
}
